import { createClientLogger, logClientError } from '@/client/logging/client-logger';
import { useCallback } from 'react';
import { toast } from '@/ui/toast';
import { createCsvExportToastId } from '@/ui/toast-ids';
import { UnsupportedMarketplaceRouteError } from '@/repositories/unsupported-marketplace-error';
import { isRequestCanceled } from '@/client/error-handling/is-request-canceled';

type CsvExportResult = {
    limitWarning?: string | null;
};

type UseCsvExportActionCoreArgs = {
    isExportLoading: boolean;
    startExport: () => string;
    finishExportSuccess: (exportOperationId: string, limitWarning?: string | null) => void;
    finishExportFailure: (exportOperationId: string) => void;
    finishExportCanceled: (exportOperationId: string) => void;
    runExport: (exportOperationId: string) => Promise<CsvExportResult>;
    successToastMessage?: string;
    failureToastMessage?: string;
};

type UseCsvExportActionCoreResult = {
    exportCsv: () => Promise<void>;
};

const csvExportLogger = createClientLogger({
    scope: 'csv-export',
});

export const useCsvExportActionCore = ({
    isExportLoading,
    startExport,
    finishExportSuccess,
    finishExportFailure,
    finishExportCanceled,
    runExport,
    successToastMessage = 'Данные выгружены',
    failureToastMessage = 'Не удалось выгрузить CSV',
}: UseCsvExportActionCoreArgs): UseCsvExportActionCoreResult => {
    const exportCsv = useCallback(async () => {
        if (isExportLoading) {
            return;
        }

        const exportOperationId = startExport();

        try {
            const result = await runExport(exportOperationId);
            const limitWarning = result.limitWarning || null;

            if (limitWarning) {
                toast.warning(limitWarning, {
                    autoClose: false,
                    toastId: createCsvExportToastId({
                        operationId: exportOperationId,
                        outcome: 'limit-warning',
                    }),
                });
            }

            finishExportSuccess(exportOperationId, limitWarning);
            toast.success(successToastMessage);
        } catch (error) {
            if (isRequestCanceled(error)) {
                finishExportCanceled(exportOperationId);
                return;
            }

            logClientError({
                logger: csvExportLogger,
                message: '[client.csv-export.export] Failed to export CSV.',
                error,
                context: {
                    exportOperationId,
                },
            });
            if (error instanceof UnsupportedMarketplaceRouteError) {
                toast.error(error.message);
            } else {
                toast.error(failureToastMessage);
            }
            finishExportFailure(exportOperationId);
        }
    }, [
        failureToastMessage,
        finishExportFailure,
        finishExportCanceled,
        finishExportSuccess,
        isExportLoading,
        runExport,
        startExport,
        successToastMessage,
    ]);

    return {
        exportCsv,
    };
};
